import { Box, type ColorProps, Flex, useBreakpointValue } from '@chakra-ui/react'
import { useMemo, useState } from 'react'

import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import useIsQueryAvailable from '@/client/hooks/useIsQueryAvailable'
import { isUserDefined, isUserOnboarding } from '@/client/services/user'

import GlobalModal from '@/components/GlobalModal/GlobalModal'
import GlobalToast from '@/components/GlobalToast/GlobalToast'
import Footer from '@/components/Layout/Footer'
import PlatformLayout, { type PlatformLayoutProps } from '@/components/Layout/PlatformLayout'

import MetaHeader, { type MetaHeaderProps } from './MetaHeader'
import Navbar from './Navbar/Navbar'
import NavbarBanner from './Navbar/NavbarBanner'
import { navBannerHeightDesktop, navBannerHeightMobile, navHeight } from './Navbar/styled'
import type { NavbarItem } from './Navbar/types'
import { LayoutContext } from './useLayoutContext'

type LayoutProps = {
  bg?: ColorProps['color']
  children: React.ReactNode
  hideLoginButton?: boolean
  hideBanner?: boolean
  hideGlobalModal?: boolean
  hideFooter?: boolean
  // eslint-disable-next-line react/no-unused-prop-types
  forceShowLoggedOutLayout?: boolean
  forceShowOnboardingNavbar?: boolean
  showSimplifiedNavbar?: boolean
  customNavbarItems?: NavbarItem[]
} & PlatformLayoutProps &
  MetaHeaderProps

function LayoutLoggedOut({
  bg = 'bg',
  children,
  hideLoginButton = false,
  hideBanner = false,
  hideGlobalModal = false,
  hideFooter = false,
  forceShowOnboardingNavbar = false,
  showSimplifiedNavbar = false,
  customNavbarItems,
}: LayoutProps): JSX.Element {
  const user = useUser()
  const isQueryAvailable = useIsQueryAvailable()

  const [showingBanner, setShowingBanner] = useState(false)

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const navbarOffsetPx = `calc(${navHeight} + ${showingBanner ? (isMobile ? navBannerHeightDesktop : navBannerHeightMobile) : '0px'})`

  const contextValue = useMemo(() => ({ navbarOffsetPx }), [navbarOffsetPx])

  return (
    <LayoutContext.Provider value={contextValue}>
      <Flex
        as="main"
        direction="column"
        mt={navbarOffsetPx}
        minH={`calc(100vh - ${navbarOffsetPx})`}
        bg={bg}
      >
        <Box position="fixed" top={0} left={0} width="100%" zIndex={5}>
          {hideBanner || !isQueryAvailable ? null : (
            <NavbarBanner setShowBanner={setShowingBanner} showingBanner={showingBanner} />
          )}
          <Navbar
            hideLoginButton={hideLoginButton}
            forceShowOnboardingNavbar={forceShowOnboardingNavbar}
            showSimplifiedNavbar={showSimplifiedNavbar}
            customNavbarItems={customNavbarItems}
          />
        </Box>
        <div id="modal" />
        {!isQueryAvailable ||
        !isUserDefined(user) ||
        hideGlobalModal ||
        isUserOnboarding(user) ? null : (
          <>
            <GlobalModal />
            <GlobalToast />
          </>
        )}
        {children}
      </Flex>
      {hideFooter ? null : <Footer />}
    </LayoutContext.Provider>
  )
}

export default function Layout({
  children,
  hideBanner,
  forceShowLoggedOutLayout = false,
  customNavbarItems,
  activeNavItem,
  header,
  ...props
}: LayoutProps): JSX.Element {
  const { dryRun } = useAppContext()
  const user = useUser()

  const layoutLoggedOutProps = {
    children,
    hideBanner,
    ...props,
  }

  const platformLayoutProps = {
    children,
    activeNavItem,
    header,
    ...props,
  }

  let layout: JSX.Element

  // Right now we will only support custom navbar items for the logged out layout
  if (customNavbarItems) {
    forceShowLoggedOutLayout = true
  }

  if (dryRun) {
    // Scan queries for both Layouts
    layout = (
      <>
        <PlatformLayout {...platformLayoutProps} />
        <LayoutLoggedOut customNavbarItems={customNavbarItems} {...layoutLoggedOutProps} />
      </>
    )
  } else if (isUserDefined(user) && !isUserOnboarding(user) && !forceShowLoggedOutLayout) {
    layout = <PlatformLayout {...platformLayoutProps} />
  } else {
    layout = <LayoutLoggedOut customNavbarItems={customNavbarItems} {...layoutLoggedOutProps} />
  }

  return (
    <>
      <MetaHeader {...props} />
      {layout}
    </>
  )
}
